import React from 'react';
import createMentionPlugin from '@draft-js-plugins/mention';
import { ContentState, Modifier, SelectionState } from 'draft-js';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { ListRow } from '@repo/components/lists';
import { Avatar } from '@repo/components/avatars';
import cn from 'classnames';
import styles from './TextEditor.module.scss';
import { useSelector } from 'react-redux';
import { selectContact } from '@repo/redux/actions/contacts';

const MENTION_PARSER_REGEX = /^\[contact:(\d+):(.[^\]]+)\]/;
const mentionMutability = 'IMMUTABLE';

export const markdownToDraftMentionEntityRules = {
  mention_open: (entity) => ({
    type: 'mention',
    mutability: mentionMutability,
    data: entity.data,
  }),
};

export const draftMentionsToMarkdownRules = {
  mention: {
    open: (entity) => `[contact:${entity.data.mention.id}:`,
    close: () => ']',
  },
};

type MentionData = { id: string; displayName: string };
export const parseMentionsFromHTML = (htmlDoc: HTMLDocument): Array<MentionData> => {
  const mentionNodes = Array.from(htmlDoc.querySelectorAll('[dataentitytype="mention"]'));
  return mentionNodes.map((node) => ({
    id: node.getAttribute('datamentionid'),
    displayName: node.getAttribute('datamentionname'),
  }));
};

function findAllIndexes(str: string, substr: string): Array<number> {
  const indexes = [];
  let matchedIdx = str.indexOf(substr, 0);

  while (matchedIdx >= 0) {
    indexes.push(matchedIdx);
    matchedIdx = str.indexOf(substr, matchedIdx + 1);
  }

  return indexes;
}

// Used when going from HTML -> ContentState. Each mention instance parsed out of the input HTML
// is used against the input ContentState to match and replace corresponding LINK entity instances
// with a mention entity instances.
export const insertParsedMentionEntities = (
  html: string,
  contentState: ContentState,
): ContentState => {
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(html, 'text/html');
  const parsedMentions = parseMentionsFromHTML(htmlDoc);

  return parsedMentions.reduce((contentStateWithMentions, mentionData) => {
    const displayName = mentionData.displayName;
    const contentBlocks = contentStateWithMentions.getBlocksAsArray();

    for (let idx = 0; idx < contentBlocks.length; idx++) {
      const block = contentBlocks[idx];
      // Find all instances in this block of the text for the given mention display name
      const mentionOffsets = findAllIndexes(block.getText(), `@${displayName}`);

      if (mentionOffsets.length == 0) {
        continue;
      }

      // For the matched instances of the display name text in the block, find any cases
      // where a text match corresponds to a LINK entity
      const existingEntityKeys = mentionOffsets.map((offset) => ({
        entityKey: block.getEntityAt(offset),
        blockKey: block.getKey(),
        offset,
      }));
      const existingEntities = existingEntityKeys
        .map((keys) => {
          return {
            ...keys,
            entity: contentStateWithMentions.getEntity(keys.entityKey),
          };
        })
        .filter(({ entity }) => entity.getType() == 'LINK');

      if (existingEntities.length == 0) {
        continue;
      }

      // For the first matched LINK entity, we get the selection and replace with a
      // mention entity created here. Any subsequent matches in the current block will
      // be replaced on subsequent iterations of the reducer function. This ensures
      // that we keep a 1:1 of parsed mentions:link entities to be replaced.
      const { blockKey, offset } = existingEntities[0];
      const selection = SelectionState.createEmpty(blockKey).merge({
        focusOffset: offset + displayName.length + 1, // add 1 to account for the @
        anchorKey: blockKey,
        anchorOffset: offset,
      });

      contentStateWithMentions.createEntity('mention', 'IMMUTABLE', { mention: mentionData });
      const newEntityKey = contentStateWithMentions.getLastCreatedEntityKey();

      return Modifier.replaceText(
        contentStateWithMentions,
        selection,
        displayName,
        null,
        newEntityKey,
      );
    }

    return contentStateWithMentions;
  }, contentState);
};

const useInAppContactNav = (contactLink: string) => {
  const navigate = useNavigate();
  return (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (contactLink) {
      navigate(contactLink);
    }
  };
};

const MentionEntity = (props) => {
  const isInRouter = useInRouterContext();
  const contact = useSelector(selectContact(props.mention.id));

  let navigateToContact = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (props.clickable) {
      window.open(`https://web.clay.earth${contact.link}`, '_blank');
    }
  };

  if (isInRouter && props.clickable) {
    navigateToContact = useInAppContactNav(contact.link);
  }

  return (
    <a
      className={cn(
        styles.mention,
        'mention-entity',
        !props.clickable && styles.noHover,
        !props.clickable && styles.notClickable,
      )}
      onClick={navigateToContact}
      href={contact.link}
      spellCheck={false}
      // TODO maybe figure out a better way to do this than attrs?
      // @ts-ignore
      dataentitytype="mention"
      datamentionid={contact.id}
      datamentionname={contact.displayName}
    >
      @{props.children}
    </a>
  );
};

export const mentionEditorPlugin = ({ clickableMentions = true }: { clickableMentions: boolean }) =>
  createMentionPlugin({
    theme: {
      mentionSuggestions: styles.mentionSuggestions,
    },
    mentionComponent: (props) => <MentionEntity clickable={clickableMentions} {...props} />,
    supportWhitespace: true,
    entityMutability: mentionMutability,
  });

export const createMentionRemarkablePlugin = (plainText: boolean) => (md) => {
  md.inline.ruler.push('mention', (state): boolean => {
    const marker = state.src[state.pos];

    if (marker == '[') {
      const match = MENTION_PARSER_REGEX.exec(state.src.slice(state.pos));

      if (match) {
        const [fullMatch, id, displayName] = match;
        const mention = { id, displayName };
        const length = fullMatch.length;
        const content = `${plainText ? '@' : ''}${displayName}`;

        state.push({ type: 'mention_open', level: state.level, data: { mention } });
        state.push({ type: 'text', content, level: state.level + 1 });
        state.push({ type: 'mention_close', level: state.level });

        state.pos += length;
        return true;
      }
    }

    return false;
  });
};

export const MentionSuggestion = (props) => {
  const { mention, theme, searchValue, isFocused, ...parentProps } = props;
  const contact = useSelector(selectContact(mention.id));

  return (
    <div {...parentProps}>
      <ListRow
        className={cn(styles.mentionOption, isFocused && styles.selectedMentionOption)}
        selected={isFocused}
      >
        {contact.displayName}
        <Avatar
          url={contact.avatarURL}
          size="sm"
          initials={contact.displayName?.[0]?.toUpperCase() || ''}
          name={contact.displayName}
          grayscale={contact.isMemorialized}
        />
      </ListRow>
    </div>
  );
};
